import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Link2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { getClass, levelFromXp } from "@/lib/rpg";
import { card, label, btnPrimary, btnSecondary } from "@/lib/styles";

// Draw the shareable card on a canvas — pure 2D text, zero deps, exports to PNG.
function drawCard(canvas, d) {
  const W = 1200, H = 630;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const lvl = levelFromXp(d.xp || 0);
  const klass = getClass(d.character_class);

  ctx.fillStyle = "#0a0f1c"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#d9a441"; ctx.lineWidth = 4; ctx.strokeRect(20, 20, W - 40, H - 40);
  ctx.strokeStyle = "rgba(217,164,65,0.22)"; ctx.lineWidth = 1; ctx.strokeRect(34, 34, W - 68, H - 68);

  ctx.fillStyle = "#f1cf78"; ctx.font = "700 30px monospace";
  ctx.fillText("PROMPTBOUND", 70, 96);
  ctx.fillStyle = "#5b657d"; ctx.font = "400 18px monospace";
  ctx.fillText("AI ENGINEER MASTERY", 70, 124);

  ctx.fillStyle = "#ece6d8"; ctx.font = "700 74px sans-serif";
  ctx.fillText((d.name || "Anonymous").slice(0, 22), 70, 250);

  ctx.fillStyle = "#d9a441"; ctx.font = "600 30px monospace";
  ctx.fillText(`${klass ? klass.name + " · " : ""}${lvl.rank} · Level ${lvl.level}`, 70, 306);

  if (d.gauntlet_wins) {
    ctx.fillStyle = "#eab308"; ctx.font = "700 24px monospace";
    ctx.fillText(`GAUNTLET CHAMPION x${d.gauntlet_wins}`, 70, 348);
  }

  const stats = [["GOLD", d.gold ?? 0], ["XP", d.xp ?? 0], ["TOPICS", d.topics_completed ?? 0]];
  stats.forEach(([k, v], i) => {
    const x = 70 + i * 370;
    ctx.fillStyle = "#f1cf78"; ctx.font = "700 82px monospace";
    ctx.fillText(String(v), x, 468);
    ctx.fillStyle = "#5b657d"; ctx.font = "600 22px monospace";
    ctx.fillText(k, x, 512);
  });

  ctx.fillStyle = "#5b657d"; ctx.font = "400 20px monospace";
  ctx.fillText("forged in the Gauntlet · promptbound", 70, H - 66);
}

export default function FlexCard() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    api.get(`/flex-card/${userId}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [userId]);

  useEffect(() => {
    if (data && canvasRef.current) drawCard(canvasRef.current, data);
  }, [data]);

  const download = () => {
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = `promptbound-${(data.name || "card").replace(/\s+/g, "-")}.png`;
    a.click();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className={label}>flex card</span>
        {err ? (
          <div className={`${card} p-8 mt-6 text-center`}>
            <p className="text-zinc-300">No flex card has been minted here.</p>
            <Link to="/account" className={`${btnSecondary} mt-5 inline-flex`}><ArrowLeft size={14} /> Back to account</Link>
          </div>
        ) : !data ? (
          <p className="text-zinc-500 font-mono mt-6">loading…</p>
        ) : (
          <>
            <canvas
              ref={canvasRef}
              data-testid="flex-canvas"
              className="mt-6 w-full border border-zinc-800 rounded-lg shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]"
              style={{ aspectRatio: "1200 / 630" }}
            />
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={download} className={btnPrimary} data-testid="flex-download"><Download size={16} /> Download PNG</button>
              <button onClick={copyLink} className={btnSecondary} data-testid="flex-copy"><Link2 size={16} /> Copy share link</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
